import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Decimal } from 'decimal.js';

import { RoommateData } from 'src/features/multi-occupancy/types';

import { LegalRepresentative, Options, SelectedPrice, Tracking } from '../types/basket-types';

export interface BasketUser {
    id: string | null;
    first_name: string;
    last_name: string;
    address1: string;
    address2: string;
    city: string;
    country: string;
    nationality: string;
    number: string;
    phone_code: string;
    post_code: string;
    study_year: string;
    university: string;
    dob: string;
    email: string;
    preferred_language: string;
    is_spanish_resident: boolean;
    nie_number: string | null;
    nif_number: string | null;
    passport_number: string | null;
    passport_issuing_country: string | null;
}

export interface BasketProduct {
    id: string;
    room_code: string;
    academic_year: string;
    building_id: string;
    development_id: string;
    price: Decimal;
    currency: string;
    deposit: Decimal;
    advance_payment_weeks: number;
    deposit_prepaid: boolean;
    selected_students: number;
    term: string;
    beds: number;
    payment_plan_id: string;
    instalment_type: string;
    cbl: boolean;
    stl: boolean;
    occupants: RoommateData[];
    selected_price: SelectedPrice;
    legal_representative: LegalRepresentative | null;
    promotion_id: string | null;
}

export interface Basket {
    id: string | null;
    user: BasketUser | null;
    products: BasketProduct[] | null;
    options: Options;
    tracking: Tracking | null;
    vendor: string | null;
    error: string | null;
    room_mates: RoommateData[] | null;
    licence_agreement_signed_at: string | null;
    booking_terms_signed_at: string | null;
    created_date: string | null;
    updated_date: string | null;
}

const initialState: Basket = {
    id: null,
    user: null,
    products: null,
    options: {
        rebooker: false,
        rebookerPhase: null,
        fillerWeekEligible: false,
    },
    tracking: null,
    vendor: null,
    error: null,
    room_mates: null,
    licence_agreement_signed_at: null,
    booking_terms_signed_at: null,
    created_date: null,
    updated_date: null,
};

const basketSlice = createSlice({
    name: 'basket',
    initialState,
    reducers: {
        setBasket: (state: Basket, action: PayloadAction<Partial<Basket>>) => ({
            ...state,
            ...action.payload,
        }),
        addBasketProduct: (state: Basket, action: PayloadAction<BasketProduct>) => {
            state.products = [action.payload];
            state.error = null;
        },
        updateBasketProduct: (state: Basket, action: PayloadAction<BasketProduct>) => {
            if (state.products) {
                const index = state.products.findIndex(
                    (product) => product.id === action.payload.id
                );
                if (index !== -1) {
                    state.products[index] = action.payload;
                }
            }
            state.error = null;
        },
        updateBasketProductPaymentplanId: (state: Basket, action: PayloadAction<string>) => {
            if (state.products && state.products[0]) {
                state.products[0].payment_plan_id = action.payload;
            }
            state.error = null;
        },
        updateBasketUser: (state: Basket, action: PayloadAction<BasketUser>) => {
            state.user = action.payload;
            state.error = null;
        },
        updateBasketRoommates: (state: Basket, action: PayloadAction<RoommateData[]>) => {
            state.room_mates = action.payload;
            state.error = null;
        },
        setBasketId: (state: Basket, action: PayloadAction<string | null>) => {
            state.id = action.payload;
        },
        updateBasketOptions: (state: Basket, action: PayloadAction<Options>) => {
            state.options = action.payload;
        },
        updateBasketTracking: (state: Basket, action: PayloadAction<Tracking | null>) => {
            state.tracking = action.payload;
        },
        setBasketVendor: (state: Basket, action: PayloadAction<string>) => {
            state.vendor = action.payload;
        },
        setBasketError: (state: Basket, action: PayloadAction<string>) => {
            state.error = action.payload;
        },
        clearBasket: (state: Basket) => {
            state.id = null;
            state.products = null;
            state.options.rebooker = false;
            state.tracking = null;
            state.vendor = null;
            state.error = null;
            state.room_mates = null;
            state.licence_agreement_signed_at = null;
            state.booking_terms_signed_at = null;
            state.created_date = null;
            state.updated_date = null;
        },
    },
});

export const {
    setBasket,
    addBasketProduct,
    updateBasketProduct,
    updateBasketProductPaymentplanId,
    updateBasketUser,
    updateBasketRoommates,
    setBasketId,
    updateBasketOptions,
    updateBasketTracking,
    setBasketVendor,
    setBasketError,
    clearBasket,
} = basketSlice.actions;

export default basketSlice.reducer;

const selectBasketState = (state: { basket: Basket }) => state.basket;

export const selectBasket = createSelector([selectBasketState], (basket) => basket);

export const selectBasketId = createSelector([selectBasketState], (basket) => basket.id);

export const selectBasketUser = createSelector([selectBasketState], (basket) => basket.user);

export const selectBasketProducts = createSelector(
    [selectBasketState],
    (basket) => basket.products
);

export const selectBasketOptions = createSelector([selectBasketState], (basket) => basket.options);

export const selectBasketTracking = createSelector(
    [selectBasketState],
    (basket) => basket.tracking
);

export const selectBasketVendor = createSelector([selectBasketState], (basket) => basket.vendor);
export const selectRoommates = createSelector(
    [selectBasketState],
    (basket) => basket.room_mates ?? []
);
